import { useLearner } from '@/app/useAppState';
import { STAGE, SubjectBadge } from '@/components/ui/Badges';
import { ButtonLink } from '@/components/ui/Button';
import { Loading } from '@/components/ui/Loading';
import { Mascot } from '@/components/ui/Mascot';
import { ScreenHeader } from '@/components/ui/ScreenHeader';
import { curriculum } from '@/content/curriculum';
import { recommendationPath, useMastery, useRecommendations } from '@/features/learning/hooks';
import { useI18n } from '@/i18n/context';
import { recommendationText } from './text';

export function ReviewScreen() {
  const learner = useLearner();
  const { t, l } = useI18n();
  const recommendations = useRecommendations(learner);
  const mastery = useMastery(learner.id);
  if (!recommendations || !mastery) return <Loading label={t('app.loading')} />;

  const items = recommendations.filter((r) => r.kind !== 'start' && r.kind !== 'next').slice(0, 8);

  return (
    <div className="flex flex-col gap-6">
      <ScreenHeader
        title={t('review.title')}
        subtitle={items.length > 0 ? t('review.intro') : undefined}
      />
      {items.length === 0 ? (
        <div className="flex flex-col items-center gap-4 py-8 text-center">
          <Mascot className="size-28" />
          <p className="text-lg">{t('review.empty')}</p>
          <ButtonLink to="/learn/subjects">{t('nav.subjects')}</ButtonLink>
        </div>
      ) : (
        <ul className="flex flex-col gap-3">
          {items.map((rec) => {
            const objective = curriculum.objective(rec.objectiveId);
            const subject = objective ? curriculum.subject(objective.subjectId) : undefined;
            if (!objective || !subject) return null;
            const status = mastery.get(objective.id)?.status ?? 'notStarted';
            return (
              <li
                key={rec.objectiveId}
                className="flex flex-col gap-3 rounded-[var(--radius-card)] bg-surface p-4 shadow-[var(--shadow-card)] sm:flex-row sm:items-center"
              >
                <div className="flex flex-1 items-start gap-3">
                  <SubjectBadge subject={subject} />
                  <div className="flex flex-col gap-1">
                    <span className="flex flex-wrap items-center gap-2 font-extrabold">
                      <span aria-hidden className="emoji">
                        {STAGE[status].emoji}
                      </span>
                      {l(objective.title)}
                      <span className="rounded-full bg-sky-100 px-2 py-0.5 text-xs text-sky-700">
                        {t(`recommend.kind.${rec.kind}`)}
                      </span>
                    </span>
                    <span className="text-sm text-ink-muted">{recommendationText(t, l, rec)}</span>
                  </div>
                </div>
                <ButtonLink to={recommendationPath(rec)} className="self-start sm:self-center">
                  {t('review.start')}
                </ButtonLink>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
